import { useSnackbar } from "@kitbird/core";
import { useCallback, useRef, useState } from "react";
import {
  useInfiniteQuery, useMutation, useQuery, useQueryClient,
} from "react-query";
import useAuth from "../../../components/Authentication/useAuth";
import {
  IGetUserAccountParams, IUserAccount,
  addMasterUserAccount,
  deleteMasterUserAccount,
  getAllUserAccount,
  getMasterUserAccountList,
} from "../../../services/userAccountService";

const useQueryParamsManager = <T>(
  params: T,
  queryKey: T[],
  resetOnChange: boolean = true,
) => {
  const queryClient = useQueryClient();
  const prevParamsRef = useRef(params);
  if (resetOnChange && JSON.stringify(prevParamsRef.current) !== JSON.stringify(params)) {
    queryClient.removeQueries(queryKey);
    prevParamsRef.current = params;
  }
};

const useInvalidateQueries = () => {
  const queryClient = useQueryClient();
  return <T>(queryKey: T[]) => {
    queryClient.invalidateQueries({ queryKey });
    queryClient.removeQueries({ queryKey });
  };
};

const useGetUserAccountActive = (params: Omit<IGetUserAccountParams, "page_number">) => {
  const queryKey = ["masterUserAccountList", params];
  useQueryParamsManager(params, queryKey);
  return useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam = 1 }) => getMasterUserAccountList({
      ...params,
      page_number: pageParam,
    }),
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    getNextPageParam: (lastPage) => {
      const meta = lastPage.metadata;
      if (!meta) return undefined;
      return meta.page < meta.total_pages ? meta.page + 1 : undefined;
    },
  });
};

const useGetUserAccountInactive = (search: string) => useQuery({
  queryKey: ["allUserAccount", search],
  queryFn: () => getAllUserAccount(search),
});

const useAddUserAccount = () => {
  const invalidateQueries = useInvalidateQueries();
  return useMutation({
    mutationFn: (payload: Omit<IUserAccount, "id">) => addMasterUserAccount(payload),
    onSuccess: async () => invalidateQueries(["masterUserAccountList"]),
  });
};

const useDeleteUserAccount = () => {
  const invalidateQueries = useInvalidateQueries();
  return useMutation({
    mutationFn: (params: {
      employee_id: string;
      update_by: string
    }) => deleteMasterUserAccount(params),
    onSuccess: async () => invalidateQueries(["masterUserAccountList"]),
  });
};

const useUserAccount = () => {
  const initialParams: Omit<IGetUserAccountParams, "page_number"> = {
    page_size: 10,
    search: "",
  };
  const { userInfo } = useAuth();
  const observer = useRef<IntersectionObserver>();
  const snackbar = useSnackbar();
  const [isModalAddOpen, toggleModalAddOpen] = useState(false);
  const [params, setParams] = useState(initialParams);
  const addUserAccount = useAddUserAccount();
  const deleteUserAccount = useDeleteUserAccount();
  const {
    data: userAccountList, isLoading, isFetching, isFetchingNextPage, fetchNextPage, hasNextPage,
  } = useGetUserAccountActive(params);
  const lastElementRef = useCallback(
    (node: HTMLTableSectionElement) => {
      if (isLoading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetching) {
          fetchNextPage();
        }
      });
      if (node) observer.current.observe(node);
    },
    [fetchNextPage, hasNextPage, isFetching, isLoading],
  );

  const onInputSearch = (search: string) => setParams((prev) => ({ ...prev, search }));

  const onDeleteClick = (data: IUserAccount) => {
    deleteUserAccount.mutate(
      { employee_id: data.employee_id, update_by: userInfo.email },
      {
        onSuccess: (res) => {
          if (res.responseCode === "00") {
            snackbar.show("Berhasil menghapus data.", "success");
          } else {
            snackbar.show(`Gagal menghapus data. ${res.responseMessage}`, "error");
          }
        },
        onError: () => {
          snackbar.show("Gagal menghapus data.", "error");
        },
      },
    );
  };

  const onSubmit = (values: Omit<IUserAccount, "id"> | undefined) => {
    if (!values) return;
    addUserAccount.mutate(
      {
        create_by: userInfo.email,
        employee_email: values.employee_email,
        employee_id: values.employee_id,
        employee_name: values.employee_name,
        client_name: values.employee_name,
        isActive: true,
      },
      {
        onSuccess: (res) => {
          toggleModalAddOpen(false);
          if (res.responseCode === "Succeed") {
            snackbar.show("Berhasil menambah data.", "success");
          } else {
            snackbar.show("Gagal menambah data.", "error");
          }
        },
        onError: () => {
          snackbar.show("Gagal menambah data.", "error");
        },
      },
    );
  };

  return {
    userInfo,
    observer,
    snackbar,
    isModalAddOpen,
    toggleModalAddOpen,
    params,
    setParams,
    addUserAccount,
    deleteUserAccount,
    userAccountList,
    isLoading,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    lastElementRef,
    onInputSearch,
    onDeleteClick,
    onSubmit,
  };
};

export {
  useGetUserAccountActive,
  useGetUserAccountInactive,
  useAddUserAccount,
  useDeleteUserAccount,
  useUserAccount,
};
